import {
  AfterViewInit,
  Component,
  ElementRef,
  inject,
  OnDestroy,
  OnInit,
  ViewChild,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Subject } from 'rxjs';
import { debounceTime, distinctUntilChanged } from 'rxjs/operators';
import dayjs from 'dayjs';

import { PunchesNavComponent } from '../../../shared/punches-nav/punches-nav.component';
import { DailyPunchesService } from '../../services/daily-punches.service';
import {
  DailyPunchFilters,
  EmployeeDailyPunch,
  OffTimeBlock,
  PunchBlock,
  ScheduledBlock,
} from '../../models/daily-punch.model';
import { WeekdayNames } from '../../../util/constant';
import { FilterService } from '../../../services/filter.service';
import { BusyService } from '../../../services/busy.service';

interface DailyPunchFiltersVm {
  companyIds: number[] | null;
  departmentIds: number[] | null;
  employeeId: number | null;
  searchTerm: string;
}

@Component({
  selector: 'app-daily-punches',
  standalone: true,
  imports: [CommonModule, FormsModule, PunchesNavComponent],
  templateUrl: './daily-punches.component.html',
  styleUrls: ['./daily-punches.component.scss'],
})
export class DailyPunchesComponent implements OnInit, AfterViewInit, OnDestroy {
  punches: EmployeeDailyPunch[] = [];
  errorMessage: string | null = null;
  weekdayNames = WeekdayNames;
  busyService = inject(BusyService);

  selectedDate: Date = new Date();

  filterService = inject(FilterService);

  filters: DailyPunchFiltersVm = {
    companyIds: null,
    departmentIds: null,
    employeeId: null,
    searchTerm: '',
  };

  calendarOpen = false;
  calendarDays: Date[] = [];
  months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  years: number[] = [];
  selectedMonth: number = this.selectedDate.getMonth();
  selectedYear: number = this.selectedDate.getFullYear();

  currentTimePercent = 0;

  private tickId: number | null = null;
  private readonly searchInput$ = new Subject<string>();
  private readonly employeeIdInput$ = new Subject<number | null>();

  @ViewChild('timelineRight', { static: false })
  private timelineRightRef?: ElementRef<HTMLElement>;

  private timelineWidthPx = 0;
  private resizeObserver: ResizeObserver | null = null;

  constructor(private readonly punchesService: DailyPunchesService) {}

  ngOnInit(): void {
    const currentYear = new Date().getFullYear();
    this.years = Array.from({ length: 20 }, (_, index) => currentYear - 10 + index);

    this.bindSearch();
    this.bindEmployeeId();
    this.loadDailyPunches();

    this.currentTimePercent = this.getCurrentTimePercent();

    this.tickId = window.setInterval(() => {
      this.currentTimePercent = this.getCurrentTimePercent();
    }, 60_000);
  }

  ngAfterViewInit(): void {
    const element = this.timelineRightRef?.nativeElement;

    if (!element || typeof ResizeObserver === 'undefined') {
      return;
    }

    const update = () => {
      const nameCol = this.readCssPxVar(element, '--name-col', 220);
      const fullWidth = element.getBoundingClientRect().width;

      this.timelineWidthPx = Math.max(0, fullWidth - nameCol);
    };

    update();

    this.resizeObserver = new ResizeObserver(() => update());
    this.resizeObserver.observe(element);
  }

  ngOnDestroy(): void {
    if (this.tickId != null) {
      clearInterval(this.tickId);
      this.tickId = null;
    }

    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
      this.resizeObserver = null;
    }

    this.searchInput$.complete();
    this.employeeIdInput$.complete();
  }

  get hasActiveFilters(): boolean {
    return (
      !!this.filters.companyIds?.length ||
      !!this.filters.departmentIds?.length ||
      this.filters.employeeId !== null ||
      !!this.filters.searchTerm.trim()
    );
  }

  onCompanyChange(value: number | string | null): void {
    const companyId = this.toNullableNumber(value);
    this.filters.companyIds = companyId !== null ? [companyId] : null;
    this.loadDailyPunches();
  }

  onDepartmentChange(value: number | string | null): void {
    const departmentId = this.toNullableNumber(value);
    this.filters.departmentIds = departmentId !== null ? [departmentId] : null;
    this.loadDailyPunches();
  }

  onEmployeeIdChange(value: number | string | null): void {
    const employeeId = this.toNullableNumber(value);
    this.employeeIdInput$.next(employeeId);
  }

  onSearchTermChange(value: string | null): void {
    const searchTerm = String(value ?? '').trimStart();
    this.searchInput$.next(searchTerm);
  }

  clearFilters(): void {
    this.filters = {
      companyIds: null,
      departmentIds: null,
      employeeId: null,
      searchTerm: '',
    };

    this.loadDailyPunches();
  }

  private bindSearch(): void {
    this.searchInput$.pipe(debounceTime(1000), distinctUntilChanged()).subscribe((value) => {
      this.filters.searchTerm = value;
      this.loadDailyPunches();
    });
  }

  private bindEmployeeId(): void {
    this.employeeIdInput$.pipe(debounceTime(1000), distinctUntilChanged()).subscribe((value) => {
      this.filters.employeeId = value;
      this.loadDailyPunches();
    });
  }

  private buildDailyPunchFilters(): DailyPunchFilters {
    return {
      companyIds: this.filters.companyIds?.length ? this.filters.companyIds : null,
      departmentIds: this.filters.departmentIds?.length ? this.filters.departmentIds : null,
      employeeId: this.filters.employeeId,
      searchTerm: this.filters.searchTerm.trim() || null,
    };
  }

  private toNullableNumber(value: number | string | null | undefined): number | null {
    if (value === null || value === undefined || value === '') {
      return null;
    }

    const parsed = Number(value);

    return Number.isFinite(parsed) && parsed > 0 ? parsed : null;
  }

  toggleCalendar(): void {
    this.calendarOpen = !this.calendarOpen;

    if (this.calendarOpen) {
      this.generateCalendar();
    }
  }

  prevMonth(): void {
    this.selectedDate = dayjs(this.selectedDate).subtract(1, 'month').toDate();
    this.selectedMonth = this.selectedDate.getMonth();
    this.selectedYear = this.selectedDate.getFullYear();
    this.generateCalendar();
  }

  nextMonth(): void {
    this.selectedDate = dayjs(this.selectedDate).add(1, 'month').toDate();
    this.selectedMonth = this.selectedDate.getMonth();
    this.selectedYear = this.selectedDate.getFullYear();
    this.generateCalendar();
  }

  onMonthChange(event: Event): void {
    const value = (event.target as HTMLSelectElement).value;

    this.selectedMonth = Number(value);
    this.selectedDate = dayjs(this.selectedDate).month(this.selectedMonth).toDate();

    this.generateCalendar();
    this.loadDailyPunches();
  }

  onYearChange(event: Event): void {
    const value = (event.target as HTMLSelectElement).value;

    this.selectedYear = Number(value);
    this.selectedDate = dayjs(this.selectedDate).year(this.selectedYear).toDate();

    this.generateCalendar();
    this.loadDailyPunches();
  }

  selectDate(date: Date): void {
    this.selectedDate = date;
    this.selectedMonth = date.getMonth();
    this.selectedYear = date.getFullYear();
    this.calendarOpen = false;
    this.loadDailyPunches();
  }

  generateCalendar(): void {
    const startOfMonth = dayjs(new Date(this.selectedYear, this.selectedMonth, 1))
      .startOf('month')
      .startOf('week');

    const endOfMonth = dayjs(new Date(this.selectedYear, this.selectedMonth, 1))
      .endOf('month')
      .endOf('week');

    const days: Date[] = [];
    let current = startOfMonth;

    while (current.isBefore(endOfMonth) || current.isSame(endOfMonth, 'day')) {
      days.push(current.toDate());
      current = current.add(1, 'day');
    }

    this.calendarDays = days;
  }

  isToday(date: Date): boolean {
    return dayjs(date).isSame(dayjs(), 'day');
  }

  isSelected(date: Date): boolean {
    return dayjs(date).isSame(dayjs(this.selectedDate), 'day');
  }

  loadDailyPunches(): void {
    this.errorMessage = null;

    this.punchesService
      .getDailyPunchesByFilters(this.selectedDate, this.buildDailyPunchFilters())
      .subscribe({
        next: (punches) => {
          this.punches = punches ?? [];
        },
        error: () => {
          this.errorMessage = 'Failed to load punches';
          this.punches = [];
        },
      });
  }

  private dayStart() {
    return dayjs(this.selectedDate).startOf('day');
  }

  private dayEnd() {
    return dayjs(this.selectedDate).endOf('day');
  }

  getBlockLeftPercent(punch: PunchBlock): number {
    const punchStart = dayjs(punch.startDate);
    const visibleStart = punchStart.isBefore(this.dayStart()) ? this.dayStart() : punchStart;
    const minutes = visibleStart.diff(this.dayStart(), 'minute');

    return (minutes / 1440) * 100;
  }

  getBlockWidthPercent(punch: PunchBlock): number {
    const punchStart = dayjs(punch.startDate);
    const punchEnd = punch.endDate ? dayjs(punch.endDate) : dayjs();
    const visibleStart = punchStart.isBefore(this.dayStart()) ? this.dayStart() : punchStart;
    const visibleEnd = punchEnd.isAfter(this.dayEnd()) ? this.dayEnd() : punchEnd;
    const minutes = visibleEnd.diff(visibleStart, 'minute');

    return Math.max(0, (minutes / 1440) * 100);
  }

  getScheduleLeftPercent(schedule: ScheduledBlock): number {
    const start = dayjs(schedule.startDate);
    const visibleStart = start.isBefore(this.dayStart()) ? this.dayStart() : start;
    const minutes = visibleStart.diff(this.dayStart(), 'minute');

    return (minutes / 1440) * 100;
  }

  getScheduleWidthPercent(schedule: ScheduledBlock): number {
    const start = dayjs(schedule.startDate);
    const end = dayjs(schedule.endDate);

    const visibleStart = start.isBefore(this.dayStart()) ? this.dayStart() : start;
    const visibleEnd = end.isAfter(this.dayEnd()) ? this.dayEnd() : end;

    const minutes = visibleEnd.diff(visibleStart, 'minute');

    return Math.max(0, (minutes / 1440) * 100);
  }

  getOffTimeLeftPercent(offTime: OffTimeBlock): number {
    const start = dayjs(offTime.startDate);
    const visibleStart = start.isBefore(this.dayStart()) ? this.dayStart() : start;
    const minutes = visibleStart.diff(this.dayStart(), 'minute');

    return (minutes / 1440) * 100;
  }

  getOffTimeWidthPercent(offTime: OffTimeBlock): number {
    const start = dayjs(offTime.startDate);
    const end = dayjs(offTime.endDate);

    const visibleStart = start.isBefore(this.dayStart()) ? this.dayStart() : start;
    const visibleEnd = end.isAfter(this.dayEnd()) ? this.dayEnd() : end;

    const minutes = visibleEnd.diff(visibleStart, 'minute');

    return Math.max(0, (minutes / 1440) * 100);
  }

  formatScheduleRange(schedule: ScheduledBlock): string {
    const start = dayjs(schedule.startDate);
    const end = dayjs(schedule.endDate);

    const startDayDiff = start.startOf('day').diff(this.dayStart(), 'day');
    const endDayDiff = end.startOf('day').diff(this.dayStart(), 'day');

    const startLabel =
      startDayDiff < 0 ? `-1d ${start.format('h:mm A')}` : start.format('h:mm A');

    const endLabel = endDayDiff > 0 ? `${end.format('h:mm A')} +1d` : end.format('h:mm A');

    return `${startLabel} – ${endLabel}`;
  }

  formatOffTimeRange(offTime: OffTimeBlock): string {
    const start = dayjs(offTime.startDate);
    const end = dayjs(offTime.endDate);

    const startDayDiff = start.startOf('day').diff(this.dayStart(), 'day');
    const endDayDiff = end.startOf('day').diff(this.dayStart(), 'day');

    const startLabel =
      startDayDiff < 0 ? `-1d ${start.format('h:mm A')}` : start.format('h:mm A');

    const endLabel = endDayDiff > 0 ? `${end.format('h:mm A')} +1d` : end.format('h:mm A');

    return `${this.getOffTimeStatusLabel(offTime)} ${startLabel} – ${endLabel}`;
  }

  getOffTimeStatusLabel(offTime: OffTimeBlock): 'Offtime Approved' | 'Offtime Pending' {
    return offTime.isApproved === true ? 'Offtime Approved' : 'Offtime Pending';
  }

  getOffTimeClass(offTime: OffTimeBlock): 'approved' | 'pending' {
    return offTime.isApproved === true ? 'approved' : 'pending';
  }

  formatRange(punch: PunchBlock): string {
    const punchStart = dayjs(punch.startDate);
    const punchEnd = punch.endDate ? dayjs(punch.endDate) : dayjs();

    const startDayDiff = punchStart.startOf('day').diff(this.dayStart(), 'day');
    const endDayDiff = punchEnd.startOf('day').diff(this.dayStart(), 'day');

    const startLabel =
      startDayDiff < 0 ? `-1d ${punchStart.format('h:mm A')}` : punchStart.format('h:mm A');

    const endLabel =
      endDayDiff > 0 ? `${punchEnd.format('h:mm A')} +1d` : punchEnd.format('h:mm A');

    return `${startLabel} – ${endLabel}`;
  }

  canShowLabel(widthPercent: number): boolean {
    if (!this.timelineWidthPx) {
      return widthPercent > 6;
    }

    const blockPx = (widthPercent / 100) * this.timelineWidthPx;
    const minPx = 74;

    return blockPx >= minPx;
  }

  canShowPunchLabel(punch: PunchBlock): boolean {
    return this.canShowLabelForText(this.getBlockWidthPercent(punch), this.formatRange(punch));
  }

  canShowScheduleLabel(schedule: ScheduledBlock): boolean {
    return this.canShowLabelForText(
      this.getScheduleWidthPercent(schedule),
      this.formatScheduleRange(schedule),
    );
  }

  canShowOffTimeLabel(offTime: OffTimeBlock): boolean {
    return this.canShowLabelForText(
      this.getOffTimeWidthPercent(offTime),
      this.formatOffTimeRange(offTime),
    );
  }

  private canShowLabelForText(widthPercent: number, text: string): boolean {
    if (!this.timelineWidthPx) {
      return widthPercent > 6;
    }

    const blockPx = (widthPercent / 100) * this.timelineWidthPx;
    const textPx = this.measureTextPx(
      text,
      '800 0.62rem -apple-system, BlinkMacSystemFont, Segoe UI, Roboto, sans-serif',
    );
    const paddingPx = 14;
    const borderSafetyPx = 10;

    return blockPx >= textPx + paddingPx + borderSafetyPx;
  }

  private measureTextPx(text: string, font: string): number {
    const trimmed = String(text ?? '').trim();

    if (!trimmed) {
      return 0;
    }

    const canvas = document.createElement('canvas');
    const context = canvas.getContext('2d');

    if (!context) {
      return trimmed.length * 7;
    }

    context.font = font;

    return context.measureText(trimmed).width;
  }

  getCurrentTimePercent(): number {
    const now = new Date();

    return ((now.getHours() * 60 + now.getMinutes()) / 1440) * 100;
  }

  previousDay(): void {
    this.selectedDate = dayjs(this.selectedDate).subtract(1, 'day').toDate();
    this.loadDailyPunches();
  }

  nextDay(): void {
    this.selectedDate = dayjs(this.selectedDate).add(1, 'day').toDate();
    this.loadDailyPunches();
  }

  get previousDayDate(): string {
    const previous = new Date(this.selectedDate);
    previous.setDate(previous.getDate() - 1);

    return previous.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
    });
  }

  get nextDayDate(): string {
    const next = new Date(this.selectedDate);
    next.setDate(next.getDate() + 1);

    return next.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
    });
  }

  isSelectedDateToday(): boolean {
    return dayjs(this.selectedDate).isSame(dayjs(), 'day');
  }

  private readCssPxVar(element: HTMLElement, varName: string, fallbackPx: number): number {
    try {
      const raw = getComputedStyle(element).getPropertyValue(varName).trim();

      if (!raw) {
        return fallbackPx;
      }

      const value = Number(String(raw).replace('px', '').trim());

      return Number.isFinite(value) ? value : fallbackPx;
    } catch {
      return fallbackPx;
    }
  }

  get selectedCompanyId(): number | null {
    return this.filters.companyIds?.length ? this.filters.companyIds[0] : null;
  }

  set selectedCompanyId(value: number | null) {
    this.filters.companyIds = value !== null ? [value] : null;
  }

  get selectedDepartmentId(): number | null {
    return this.filters.departmentIds?.length ? this.filters.departmentIds[0] : null;
  }

  set selectedDepartmentId(value: number | null) {
    this.filters.departmentIds = value !== null ? [value] : null;
  }
}